/* eslint-disable max-lines */
import { isHsaFsaEligible } from "@/components/vendor/Truemed/TruemedWidget";
import {
  BOOSTERCATEGORY,
  BOOSTERNAME,
  CARENAME,
  CORENAME,
  SCALPNAME,
  SKINCATEGORY,
  SKINNAME,
} from "@/constants/names";
import { ACTIVECLEANSE_SKUS, HAIRSERUM_SKUS, PROMOSKU } from "@/constants/skus";
import { AttributeMetadataDictionary } from "@/queries/customAttributeMetadata";
import {
  CartData,
  CartItem,
  CartQuote,
  ChildProductDetail,
  CartActionType,
  CartState,
  CART_ACTION_TYPES,
  ChildProducts,
} from "@/types/cart";
import type { CheckoutItem, GuestCartQuery } from "@/types/checkout";
import { BusinessLine, EventData, ProductLine } from "@/types/common";
import { readCookie } from "@/utils/cookies";
import { isSubCasted, unescapeProductDisplayText } from "@/utils/general";
import { getMaskedId } from "@/utils/getMaskedId";
import { segmentEvent } from "@/utils/segmentfunctions";

export const COUPON_FAILURE_REASON = "Sorry, the discount code you entered is not valid.";
export const COUPON_REQUIRED = "Discount code is required.";

export const getCartData = async (): Promise<CartData> => {
  /*
   * GET /V1/nutrafol-carts (no ID) resolves the cart through Magento's CheckoutSession, which
   * reads the PHP session cookie (PHPSESSID) and looks up the quote_id stored in that session.
   * It does NOT read a masked_id cookie directly.
   *
   * This means the session-based endpoint only works when the browser established a Magento
   * session beforehand (e.g. via a DPA redirect). It returns "Quote does not exist." when:
   *   - Running locally (cross-origin fetch — browser won't send staging cookies at all)
   *   - Running E2E tests (cart created server-side via API — no browser session established)
   *
   * GET /V1/nutrafol-carts/{masked_id} resolves by masked_id directly, bypassing the PHP
   * session, and works in all environments. We prefer this form whenever the masked_id is
   * available: live cookie first (set by any prior add-to-cart or E2E test helper), then
   * the NEXT_PUBLIC_MASKED_ID env var (manual local dev fallback).
   * https://nutrafol.atlassian.net/wiki/spaces/NS/pages/1787920385/Cart+-+working+locally
   */
  let cartEndpoint = `${process.env.NEXT_PUBLIC_MAGENTO_URL}rest/V1/nutrafol-carts/`;
  const maskedId = getMaskedId();

  if (maskedId) {
    cartEndpoint = `${process.env.NEXT_PUBLIC_MAGENTO_URL}rest/V1/nutrafol-carts/${maskedId}`;
  }
  const response = await fetch(cartEndpoint);

  if (!response.ok) {
    throw new Error(`Failed to fetch cart data: ${response.status}`);
  }

  // magento API returns a JSON string, so we need to parse it twice :/
  const jsonString = await response.json();
  const data = typeof jsonString === "string" ? JSON.parse(jsonString) : jsonString;
  const spItem = Object.keys(data?.items ?? [])
    .filter(Boolean)
    .find((el) => el?.includes("SP01"));
  /*
   * data.hide_checkout_discount_field = true;
   * data.hide_checkout_discount_text = "2 complimentary gifts with your scalp care purchase";
   */
  if (spItem) {
    data.hide_checkout_discount_field = true;
  }
  const cartItemsWithReportingData = itemsWithReportingAttributes(data.items || {});
  return {
    ...data,
    items: cartItemsWithReportingData,
  };
};

/** Maps a product_category string label to its display name. Shared by cart and checkout. */
export const formatCategoryLabel = (productCategory: string | null | undefined): string => {
  if (productCategory === SKINCATEGORY) return SKINNAME;
  if (productCategory === BOOSTERCATEGORY) return BOOSTERNAME;
  if (productCategory === SCALPNAME) return CARENAME;
  return CORENAME;
};

export const formatCategoryName = (product: CartItem | ChildProductDetail) => {
  const { product_category } = product;
  const isPromo = PROMOSKU.includes(product.sku);
  let formattedCategory = formatCategoryLabel(product_category);
  if (isPromo && "child_products" in product && product.child_products) {
    const arr: string[] = [];
    for (const k in product.child_products) {
      const child = product.child_products[
        k as keyof typeof product.child_products
      ] as unknown as ChildProductDetail;
      arr.push(child.name.replace(/\\/g, "") + " (Qty:" + child.qty + ")");
    }
    formattedCategory = arr.join(", ");
  }
  return formattedCategory;
};

const segmentMap = {
  "1 month": {
    billing_interval: "1 month",
    subscription_type: "Monthly",
  },
  "3 months": {
    billing_interval: "3 month",
    subscription_type: "3 Month",
  },
  "6 months": {
    billing_interval: "6 month",
    subscription_type: "6 Month",
  },
};

export const itemsWithReportingAttributes = (items: Record<string, CartItem>) => {
  const itemsWithReporting: Record<string, CartItem> = {};
  Object.keys(items).forEach((item) => {
    const product = items[item];
    let productWithAttributes = { ...product };
    if (isSubCasted(product)) {
      productWithAttributes = {
        ...productWithAttributes,
        ...segmentMap[product.shipping_interval as keyof typeof segmentMap],
      };
    }
    productWithAttributes.quote = productWithAttributes.quote || ({} as CartQuote);
    itemsWithReporting[item as string] = productWithAttributes;
  });
  return itemsWithReporting;
};

export const isProductInCart = (cartData: CartData | undefined, sku: string): boolean => {
  if (!cartData) return false;
  return Object.values(cartData.items).some((item) => item.quote?.sku === sku);
};

export const isAnyProductInCart = (cartData: CartData | undefined, skus: string[]): boolean => {
  if (!cartData) return false;
  return Object.values(cartData.items).some((item) => skus.includes(item.sku));
};

/**
 * Returns true if any cart item has a purchase_source in the given list.
 * Checks both item.purchase_source and item.quote?.purchase_source.
 */
export const cartHasAnyPurchaseSource = (
  cartData: CartData | undefined,
  sources: string[]
): boolean => {
  if (!cartData?.items || sources.length === 0) return false;
  return Object.values(cartData.items).some((item) => {
    const source = item.quote?.purchase_source ?? item.purchase_source;
    return source && sources.includes(source);
  });
};

export const getCartSkus = (cartData: CartData | undefined): string[] => {
  if (!cartData) return [];
  return Object.values(cartData.items).map((item) => item.sku);
};

/**
 * True only when the item is HSA/FSA eligible.
 * Items flagged `is_gift` and $0 items are excluded.
 */
export const isItemHsaFsaEligible = (item: CartItem): boolean =>
  !item.is_gift &&
  parseFloat(item.price) !== 0 &&
  isHsaFsaEligible(item.quote?.hsa_fsa_eligibility);

export const getCartItemBySku = (
  cartData: CartData | undefined,
  sku: string
): CartItem | undefined => {
  if (!cartData) return undefined;
  return Object.values(cartData.items).find((item) => item.sku === sku);
};

// Specific product category checking functions
export const isHairSerumInCart = (cartData: CartData | undefined): boolean => {
  if (!cartData) return false;
  return Object.values(cartData.items).some((item) => HAIRSERUM_SKUS.includes(item.sku));
};

export const isActiveCleanseInCart = (cartData: CartData | undefined): boolean => {
  if (!cartData) return false;
  return Object.values(cartData.items).some((item) => ACTIVECLEANSE_SKUS.includes(item.sku));
};

export const isPackageCasted = (product: CartItem): boolean => {
  return parseInt(product?.is_package_product) === 1;
};

export const isItemQuizReco = (item: CartItem): boolean => {
  return /bbq|quiz/.test(item?.quote?.purchase_source);
};

/**
 * Magento attaches some gifts from `purchase_source` rather than gift SKUs.
 * Cart upgrades re-add via DPA, which defaults to `cart` and would drop those
 * gifts unless the original source is passed back.
 *
 * Preserves BBQ quiz sources (`bbq_*`, e.g. `bbq_women_brush`) and Start Strong
 * Set sources (`*_startstrongkit`). Returns null for any other source.
 */
export const getGiftEntitlingPurchaseSource = (item?: CartItem): string | null => {
  const source = (item?.quote?.purchase_source ?? item?.purchase_source)?.trim();
  if (!source) return null;
  const normalized = source.toLowerCase();
  if (normalized.startsWith("bbq_") || normalized.includes("startstrongkit")) {
    return source;
  }
  return null;
};

/** @deprecated Use getGiftEntitlingPurchaseSource */
export const getBbqPurchaseSource = getGiftEntitlingPurchaseSource;

export const isCheckoutItemQuizReco = (item: CheckoutItem): boolean => {
  return /bbq|quiz/.test(item?.purchase_source);
};

export const getSegmentQuizRecoItems = (items: Record<string, CartItem>): boolean => {
  let quizReco = false;
  Object.values(items).forEach((item) => {
    if (isItemQuizReco(item)) {
      quizReco = true;
    }
  });
  return quizReco;
};

export const getCheckoutQuizRecoItems = (items: CheckoutItem[]): boolean => {
  let quizReco = false;
  Object.values(items).forEach((item) => {
    if (isCheckoutItemQuizReco(item)) {
      quizReco = true;
    }
  });
  return quizReco;
};

export const getSegmentProducts = (
  items: Record<string, CartItem>,
  coupon_code?: string
): EventData[] => {
  if (typeof items !== "object") return [];
  return Object.keys(items).map((item) => ({
    ...getProductObjectItems(items[item], coupon_code),
  }));
};

export const getKlaviyoValues = (products: EventData[]) => {
  return {
    product_names: products.map((product) => product?.name),
    product_core_categories: products.map((product) => product?.core_category),
    product_image_urls: products.map((product) => product?.image_url),
  };
};

export const getSegmentCheckoutProducts = (items: CheckoutItem[]): EventData[] => {
  if (typeof items !== "object") return [];
  return items.map((item: CheckoutItem) => ({
    ...getCheckoutObjectItems(item),
  }));
};
export const getCartState = (items: Record<string, CartItem> | CheckoutItem[]): CartState => {
  if (typeof items !== "object") {
    return null;
  }

  const products = Object.values(items);

  if (products?.length === 0) {
    return null;
  }

  const hasSub = products.some((p) => p.is_subscription);
  const hasOneTime = products.some((p) => !p.is_subscription);

  if (hasSub) {
    if (hasOneTime) {
      return "mixed";
    } else {
      return "subscription_only";
    }
  }
  return "one_time_only";
};

export const getOrderCartState = (items: GuestCartQuery["cart"]["items"]): CartState => {
  if (items?.length === 0) {
    return null;
  }

  const hasSub = items.some((p) => !!p.product?.billing_interval);
  const hasOneTime = items.some((p) => !p.product?.billing_interval);

  if (hasSub) {
    if (hasOneTime) {
      return "mixed";
    } else {
      return "subscription_only";
    }
  }
  return "one_time_only";
};

export const getOrderCartProductLine = (
  items: GuestCartQuery["cart"]["items"] | CartItem[]
): ProductLine | undefined => {
  if (!items?.length) return undefined;

  const names = items.map((item) => ("product" in item ? item.product?.name : item.name));

  // Check for "Men" presence and exclusivity
  const hasAnyMen = names.some((name) => name?.includes("Men"));
  const hasOnlyMen = names.every((name) => name?.includes("Men"));
  if (hasOnlyMen) return "men";
  if (hasAnyMen) return "neutral";
  return "women";
};

export const getOrderCartBusinessLine = (
  items: GuestCartQuery["cart"]["items"] | CartItem[]
): BusinessLine | undefined => {
  if (!items?.length) return undefined;

  // TypeScript guard for Cart|Checkout
  const names = items.map((item) => ("product" in item ? item.product?.name : item.name));

  // Check for "Skin" presence and exclusivity
  const hasAnySkin = names.some((name) => name?.includes("Skin"));

  if (hasAnySkin) return "mixed";
  return "Hair";
};

export const getProductObjectItems = (product: CartItem, coupon_code?: string): EventData => {
  const quote = product?.quote;
  const quantity = Number(quote?.qty) || 1;

  // Identify the unit-level discount safely
  const rawDiscount = Number(quote?.item_discount);
  const totalRowDiscount = Number(quote?.row_old_price_total) - Number(quote?.row_total);

  let discountPerUnit = 0;
  if (Number.isFinite(rawDiscount) && rawDiscount > 0) {
    discountPerUnit = rawDiscount;
  } else if (totalRowDiscount > 0 && quantity) {
    discountPerUnit = totalRowDiscount / quantity;
  }

  // Determine the base and final price
  const cartUnitPrice = parseFloat(String(quote?.price || product.price || 0));
  const originalOldPriceTotal = Number(quote?.row_old_price_total || 0);

  const finalPricePerUnit = cartUnitPrice;

  /*
   * If cartUnitPrice is already the discounted price, do NOT subtract the discount again.
   * We can check if the row_old_price matches our calculation to confirm if it's already discounted.
   */
  if (originalOldPriceTotal > 0) {
    const originalUnitOldPrice = originalOldPriceTotal / quantity;

    if (Math.abs(cartUnitPrice - originalUnitOldPrice) < 0.01) {
      /*
       * cartUnitPrice is still the original full price, so we need to subtract the discount
       * per convo 6/12/26 with Hong decided to show an actual price.
       * finalPricePerUnit = cartUnitPrice - discountPerUnit;
       */
    }
    //If it's already discounted, it remains `cartUnitPrice`
  }

  // 3. Formatting modifications
  const sku = (quote?.sku || product.sku || "").replace("-SP", "-S");
  const purchase_source = quote?.purchase_source;

  const isMen = product.product_gender === "Men";
  const isBrand = product.product_gender === "All";
  const product_line = (isBrand ? "neutral" : isMen ? "men" : "women") as ProductLine;

  return {
    billing_interval: product?.billing_interval,
    shipping_interval: product?.shipping_interval,
    category: formatCategoryName(product),
    core_category: product.core_category,
    // Only send coupon code if a discount was actually applied to this item
    coupon: discountPerUnit > 0 && coupon_code ? coupon_code : null,
    discount: typeof discountPerUnit === "number" ? +discountPerUnit?.toFixed(2) : 0,

    id: sku,
    image_url: product.label1,
    name: unescapeProductDisplayText(product.name),
    price: finalPricePerUnit,
    purchase_source,
    quiz_reco: isItemQuizReco(product),
    product_id: product.entity_id,
    quantity,
    source: purchase_source,
    sku,
    url: product.url,
    variant: isSubCasted(product) ? "subscription" : "one-time",
    product_line,
    product_gender: product.product_gender,
    product_category: product.product_category,
    purchase_type: product.purchase_type,
    subscription_type: product.subscription_type,
  };
};

const getCheckoutObjectItems = (item: CheckoutItem): EventData => {
  if (!item?.product) {
    return null;
  }

  const old_price = item.product?.old_price;
  const price = item.prices.price?.value;
  const discount = old_price > price ? old_price - price : 0;

  const sku = item.product?.sku;
  const res = {
    name: item.product.name,
    discount: discount.toFixed(2),
    price,
    product_id: item.id,
    sku: item.product.sku,
    quantity: item.quantity,
    category: item.product?.product_category,
    product_category: item.product?.product_category,
    core_category: item.product?.product_core_category,
    product_gender: item.product?.product_gender,
    purchase_type: item.product?.purchase_type,
    billing_interval: item.product?.billing_interval,
    shipping_interval: item.product?.billing_interval,
    billing_structure: item.product?.billing_interval,
    purchase_source: item?.purchase_source,
    quiz_reco: isCheckoutItemQuizReco(item),
    variant: item.product?.billing_interval ? "subscription" : "one-time",
    business_line: sku?.includes("SKIN") ? ["Skin"] : ["Hair"],
    image_url: item.product.image?.url,
  };
  /*
   * console.log("item", item);
   * console.log("res", res);
   */
  return res;
};

export const getProductObject = (product: CartItem): EventData => {
  return {
    category: formatCategoryName(product),
    core_category: product.core_category,
    image_url: product.label1,
    name: unescapeProductDisplayText(product.name),
    price: parseInt(String(product.quote?.price || product.price)),
    product_id: product.entity_id,
    product_category: product.product_category,
    product_gender: product.product_gender,
    purchase_type: product.purchase_type,
    quantity: 1,
    quiz_reco: isItemQuizReco(product),
    sku: product.quote?.sku || product.sku,
    source: "cart",
    url: product.url,
    // type: "button",
    variant: isSubCasted(product) ? "ongoing" : "single bottle",
    business_line: product.business_line,
  };
};

export const upsellProductRemoved = (
  product: CartItem,
  source: string,
  isRemoveButton: boolean,
  couponCode: string = ""
): void => {
  const qty = product?.quote?.qty;
  const mappedProductObject = getProductObject(product);

  segmentEvent("Upsell Product Removed", {
    products: [
      isRemoveButton
        ? mappedProductObject
        : {
            ...mappedProductObject,
            quantity: Number(qty),
            text: "Remove",
            type: "button",
            ...(couponCode && { code: couponCode }),
          },
    ],
    source,
  });
};

// cart API functions

const MAGENTO_URL = process.env.NEXT_PUBLIC_MAGENTO_URL;
const BEARER_TOKEN = process.env.NEXT_PUBLIC_MAGENTO_API_BEARER_TOKEN;

/**
 * assemble shared headers
 */
export const getMagentoHeaders = (additionalHeaders?: Record<string, string>) => {
  return {
    Accept: "application/json",
    Authorization: `Bearer ${BEARER_TOKEN}`,
    ...additionalHeaders,
  };
};

/**
 * assemble the API URL
 */
export const getMagentoUrl = (endpoint: string): string => {
  // remove leading slash if present
  const cleanEndpoint = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;
  return `${MAGENTO_URL}${cleanEndpoint}`;
};

export const magentoFetch = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  const url = getMagentoUrl(endpoint);
  const headers = getMagentoHeaders(options.headers as Record<string, string> | undefined);

  const response = await fetch(url, {
    ...options,
    headers: {
      ...headers,
      ...(options.headers || {}),
    },
  });

  return response;
};

export const applyDiscountCode = async (
  cartId: string | undefined,
  discountCode: string
): Promise<Response> => {
  if (!cartId) {
    throw new Error("Cart ID is required to apply discount code");
  }
  if (!discountCode || discountCode.trim() === "") {
    throw new Error(COUPON_REQUIRED);
  }
  return magentoFetch(`rest/V1/carts/${cartId}/coupons/${discountCode}`, {
    method: "PUT",
  });
};

export const removeDiscountCode = async (cartId: string | undefined): Promise<Response> => {
  if (!cartId) {
    throw new Error("Cart ID is required to remove discount code");
  }
  return magentoFetch(`rest/V1/carts/${cartId}/coupons`, {
    method: "DELETE",
  });
};

// For use in guest checkout
export const applyDiscountCodeGuest = async (
  maskedId: string | undefined,
  discountCode: string
): Promise<Response> => {
  if (!maskedId) {
    throw new Error("Cart ID is required to apply discount code");
  }
  if (!discountCode || discountCode.trim() === "") {
    throw new Error("Discount code is required");
  }
  return magentoFetch(`rest/V1/guest-carts/${maskedId}/coupons/${discountCode}`, {
    method: "PUT",
  });
};

// For use in guest checkout
export const removeDiscountCodeGuest = async (maskedId: string | undefined): Promise<Response> => {
  if (!maskedId) {
    throw new Error("Cart ID is required to remove discount code");
  }
  return magentoFetch(`rest/V1/guest-carts/${maskedId}/coupons`, {
    method: "DELETE",
  });
};

// For use in logged in customer checkout
export const applyDiscountCodeCustomer = async (discountCode: string): Promise<Response> => {
  const customerToken = readCookie("gatsbytoken");
  if (!customerToken) {
    throw new Error("Customer token not found. User may not be logged in.");
  }
  if (!discountCode || discountCode.trim() === "") {
    throw new Error("Discount code is required");
  }
  const url = getMagentoUrl(`rest/default/V1/carts/mine/coupons/${discountCode}`);
  return fetch(url, {
    method: "PUT",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${customerToken}`,
    },
  });
};

// For use in logged in customer checkout
export const removeDiscountCodeCustomer = async (): Promise<Response> => {
  const customerToken = readCookie("gatsbytoken");
  if (!customerToken) {
    throw new Error("Customer token not found. User may not be logged in.");
  }
  const url = getMagentoUrl(`rest/default/V1/carts/mine/coupons`);
  return fetch(url, {
    method: "DELETE",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${customerToken}`,
    },
  });
};

export const updateCartItemQuantity = async (
  cartId: string | undefined,
  itemId: string | undefined,
  quantity: number
): Promise<Response> => {
  if (!cartId) {
    throw new Error("Cart ID is required to update item quantity");
  }
  if (!itemId) {
    throw new Error("Item ID is required to update item quantity");
  }
  if (quantity <= 0) {
    throw new Error("Quantity must be greater than 0");
  }
  return magentoFetch(`rest/V1/carts/${cartId}/items/${itemId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json; charset=UTF-8",
    },
    // @ts-expect-error - withCredentials is needed for Magento API
    withCredentials: true,
    body: JSON.stringify({
      cartItem: {
        quoteId: Number(cartId),
        qty: quantity,
      },
    }),
  });
};

export const removeCartItem = async (
  cartId: string | undefined,
  itemId: string | undefined
): Promise<Response> => {
  if (!cartId) {
    throw new Error("Cart ID is required to remove item");
  }
  if (!itemId) {
    throw new Error("Item ID is required to remove item");
  }
  return magentoFetch(`rest/V1/carts/${cartId}/items/${itemId}`, {
    method: "DELETE",
    // @ts-expect-error - withCredentials is needed for Magento API
    withCredentials: true,
  });
};

export const getParamsFromUrl = (url: string) => {
  const [path, queryString] = url.split("?");
  const urlParams = new URLSearchParams(queryString);

  const savedCode = urlParams?.get("code");
  const cartId = urlParams.get("cartId");
  const itemId = urlParams.get("itemId");
  const actionType = urlParams?.get("action") as CartActionType | null;
  const isUpdate = [CART_ACTION_TYPES.INCREASE, CART_ACTION_TYPES.DECREASE].includes(actionType);
  // To get the "clean" URL we remove ajax params
  urlParams.delete("action");
  urlParams.delete("cartId");
  urlParams.delete("itemId");

  const cleanUrl = `${path}?${decodeURIComponent(urlParams.toString())}`;
  return { actionType, savedCode, isUpdate, cartId, itemId, cleanUrl };
};

/** CheckoutPage needs ATTRIBUTE_METADATA from magento for GA4 custom attribute values. */
export const meshCheckoutItems = ({
  items,
  attrDictionary,
}: {
  items: CheckoutItem[];
  attrDictionary: AttributeMetadataDictionary;
}) => {
  const getLabel = (groupKey: keyof typeof attrDictionary, valueToFind: string | number | null) => {
    if (!valueToFind) return "";
    const found = attrDictionary[groupKey]?.find(
      (item) => String(item.value) === String(valueToFind)
    );
    return found ? found.label : String(valueToFind);
  };

  return (
    items?.map((item: CheckoutItem) => {
      return {
        ...item,
        product: {
          ...item.product,
          /*
           *  --- Overwrite with lookups from Dictionary ---
           */
          product_category: getLabel("product_category", item.product?.product_category),
          product_core_category: getLabel(
            "product_core_category",
            item.product?.product_core_category
          ),
          product_gender: getLabel("product_gender", item.product?.product_gender),
          shipping_interval: getLabel("shipping_interval", item.product?.shipping_interval),
        },
      };
    }) ?? []
  );
};
export function mapCheckoutToCartItem(checkoutItem: CheckoutItem): CartItem {
  const { product, prices, child_products, id, purchase_source } = checkoutItem;

  return {
    // Top-level direct mappings & fallbacks
    entity_id: id,
    sku: product?.sku ?? "",
    name: product?.name ?? "",
    purchase_source: purchase_source,

    // Type Conversions (Numbers/Booleans to Strings/Binary Flags)
    price: prices?.price?.value?.toString() ?? "0",
    old_price: product?.old_price?.toString() ?? "0",
    is_subscription: product?.subscription_type ? 1 : 0,
    admin_only: 0, // Default fallback
    is_package_product: product?.package_product?.toString() ?? "0",

    // Product Object Mappings
    billing_interval: product?.billing_interval ?? "",
    shipping_interval: product?.shipping_interval?.toString() ?? "",
    product_gender: product?.product_gender ?? "",
    product_category: product?.product_category ?? "",
    purchase_type: product?.purchase_type ?? "",
    subscription_type: product?.subscription_type ?? "",
    url: product?.image?.url ?? "",
    business_line: "Hair",

    // Complex Object Mappings
    core_category: product?.product_core_category?.toString() ?? "",
    is_core: !!product?.product_core_category,

    // Child Products Transform
    child_products: child_products as ChildProducts,

    // Defaults for fields missing in CheckoutItem
    label1: "",
    url_key: "",
    three_pack_sku: "",
    has_ga: false,
    is_bundle: false,
    is_skin: false,
    short_description: null,
    is_gift: false,
    quote: {} as CartQuote,
  };
}
